import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SavedSearches() {
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedSearches();
  }, []);

  const fetchSavedSearches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/saved-searches`);
      setSearches(response.data);
    } catch (error) {
      console.error('Error fetching saved searches:', error);
      toast.error('Failed to load saved searches');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (searchId) => {
    try {
      await axios.delete(`${API_URL}/api/saved-searches/${searchId}`);
      setSearches(searches.filter(s => s.id !== searchId));
      toast.success('Search deleted');
    } catch (error) {
      toast.error('Failed to delete search');
    }
  };

  const applySearch = (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    navigate(`/catalog?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="saved-searches-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold mb-2">
            Saved Searches
          </h1>
          <p className="text-muted-foreground">
            {searches.length} {searches.length === 1 ? 'search' : 'searches'} saved
          </p>
        </div>

        {searches.length === 0 ? (
          <Card className="p-12 text-center border-border bg-card" data-testid="saved-searches-empty-state">
            <p className="text-muted-foreground mb-4">No saved searches yet</p>
            <Button onClick={() => navigate('/catalog')} data-testid="saved-searches-browse-button">
              Browse Watches
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="saved-searches-list">
            {searches.map((search) => (
              <Card key={search.id} className="p-6 border-border bg-card" data-testid="saved-search-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground mb-2">{search.name}</h3>
                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                      {Object.entries(search.filters).map(([key, value]) => (
                        value && (
                          <span key={key} className="px-2 py-1 rounded bg-muted">
                            {key}: {value}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => applySearch(search.filters)}
                      data-testid="saved-search-apply-button"
                    >
                      Apply
                    </Button>
                    <button
                      onClick={() => handleDelete(search.id)}
                      className="p-2 hover:bg-muted rounded transition-colors"
                      data-testid="saved-search-delete-button"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}